import { useCallback, useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import { fieldTrackingService } from "../../services/fieldTracking.service";
import {
  shouldPromptForLocation,
  shouldShareLiveLocation,
} from "../../utils/fieldTrackingPermissions";
import { displayUserName } from "../../utils/displayUserName";
import LocationPermissionModal from "./LocationPermissionModal";
import {
  isLocationValidated,
  markLocationPermissionGranted,
  markLocationValidated,
  storeMyLastLocation,
} from "../../constants/locationPermission";

const REQUIRED_MESSAGE =
  "Location access is required to continue. Please enable your location.";

const SHARE_MESSAGE =
  "Enable location so your name appears on the Field map for your agency.";

function geoDeniedMessage(err) {
  if (err?.code === 1) {
    return "Location permission denied. Enable location for this site in the browser, then tap Enable location again.";
  }
  if (err?.code === 2) {
    return "GPS is unavailable. Turn on location services, then tap Enable location again.";
  }
  if (err?.code === 3) {
    return "Location request timed out. Try again with a stronger signal.";
  }
  return "Could not get your location. Enable location and try again.";
}

function publishLocation(coords) {
  storeMyLastLocation(coords);
  markLocationPermissionGranted();
  markLocationValidated();
  window.dispatchEvent(new CustomEvent("repo:location-granted"));
  window.dispatchEvent(new CustomEvent("repo:my-location", { detail: coords }));
}

export default function FieldLocationPrompt({ children = null, blockApp = false }) {
  const { auth, logout } = useAuth();
  const role = auth?.user?.role;
  const required = shouldPromptForLocation(role);
  const share = shouldShareLiveLocation(role);

  const [ready, setReady] = useState(!required);
  const [offerShare, setOfferShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const captureAndSave = useCallback(async () => {
    if (!auth?.token) {
      throw new Error("Not signed in.");
    }
    if (!navigator.geolocation) {
      throw Object.assign(new Error("Geolocation is not supported on this device."), { code: 2 });
    }

    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
    });

    const { latitude, longitude, accuracy } = pos.coords;
    const res = await fieldTrackingService.postMyLocation(
      { latitude, longitude, accuracy },
      auth.token
    );
    const saved = res?.data?.lastKnownLocation || {
      latitude,
      longitude,
      accuracy,
      updatedAt: new Date().toISOString(),
    };
    publishLocation({
      latitude: saved.latitude,
      longitude: saved.longitude,
      accuracy: saved.accuracy,
      updatedAt: saved.updatedAt,
      tracerId: String(auth.user?.id || auth.user?._id || ""),
      name: displayUserName(auth.user),
      role,
    });
    return saved;
  }, [auth?.token, auth?.user, role]);

  useEffect(() => {
    if (!share || !auth?.token) {
      setReady(!required);
      setOfferShare(false);
      return;
    }

    // Never auto-read GPS. Agents must tap Enable on login (once per session).
    if (isLocationValidated()) {
      setReady(true);
      setOfferShare(false);
      return;
    }

    setReady(false);
    setOfferShare(!required);
  }, [share, required, auth?.token]);

  const handleEnable = async () => {
    setBusy(true);
    setError("");
    try {
      await captureAndSave();
      setReady(true);
      setOfferShare(false);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          geoDeniedMessage(err) ||
          "Could not save your location. Enable location and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!share) {
    return children;
  }

  const blocking = required && blockApp && !ready;

  return (
    <>
      {blocking ? (
        <div className="field-location-gate">
          <LocationPermissionModal
            open
            mandatory
            title="Enable location"
            description={REQUIRED_MESSAGE}
            allowLabel="Enable location"
            onAllow={handleEnable}
            busy={busy}
            error={error}
          />
          <button type="button" className="field-location-gate-logout" onClick={logout}>
            Logout
          </button>
        </div>
      ) : (
        <>
          {required && !ready ? (
            <LocationPermissionModal
              open
              mandatory
              title="Enable location"
              description={REQUIRED_MESSAGE}
              allowLabel="Enable location"
              onAllow={handleEnable}
              busy={busy}
              error={error}
            />
          ) : null}
          {offerShare ? (
            <LocationPermissionModal
              open
              title="Enable location"
              description={SHARE_MESSAGE}
              allowLabel="Enable location"
              onAllow={handleEnable}
              onDismiss={() => setOfferShare(false)}
              busy={busy}
              error={error}
            />
          ) : null}
          {children}
        </>
      )}
    </>
  );
}
